import { useCallback, useEffect, useRef, useState } from 'react';
import { attentionError } from './model.js';
const initial = (owner) => ({ owner, snapshot: null, fresh: false, loading: false, unavailable: false, error: '', readBusy: false, pendingRead: null });
const statusOf = (error) => error && typeof error === 'object' && 'status' in error ? error.status : undefined;
/** One account-scoped, complete server projection. Reading never resolves a question. */
export function useAttentionInbox({ api, accountKey, enabled, onAcknowledged }) {
    const acknowledged = useRef(onAcknowledged);
    acknowledged.current = onAcknowledged;
    const identity = useRef({ api, accountKey, enabled });
    if (identity.current.api !== api || identity.current.accountKey !== accountKey || identity.current.enabled !== enabled)
        identity.current = { api, accountKey, enabled };
    const owner = identity.current;
    const [state, setState] = useState(() => initial(owner));
    const current = state.owner === owner && enabled && accountKey ? state : initial(owner);
    const latest = useRef(current);
    latest.current = current;
    const requests = useRef(new Set());
    const sequence = useRef(0);
    const reading = useRef(null);
    const refresh = useCallback(async () => {
        if (!owner.enabled || !owner.accountKey || identity.current !== owner)
            return;
        const order = ++sequence.current, controller = new AbortController();
        requests.current.add(controller);
        setState(previous => ({ ...(previous.owner === owner ? previous : initial(owner)), loading: true }));
        try {
            const snapshot = await owner.api.snapshot(controller.signal);
            if (controller.signal.aborted || identity.current !== owner || order !== sequence.current)
                return;
            setState(previous => ({ ...(previous.owner === owner ? previous : initial(owner)), snapshot, fresh: true, loading: false, unavailable: false, error: '' }));
        }
        catch (error) {
            if (controller.signal.aborted || identity.current !== owner || order !== sequence.current)
                return;
            const unavailable = [401, 403, 404].includes(Number(statusOf(error)));
            setState(previous => ({ ...(previous.owner === owner ? previous : initial(owner)), snapshot: null,
                fresh: false, loading: false, unavailable, error: unavailable ? '' : attentionError(error) }));
        }
        finally {
            requests.current.delete(controller);
        }
    }, [owner]);
    useEffect(() => {
        setState(initial(owner));
        reading.current = null;
        if (!enabled || !accountKey)
            return;
        void refresh();
        const visibleRefresh = () => { if (document.visibilityState !== 'hidden')
            void refresh(); };
        const offline = () => {
            ++sequence.current;
            for (const controller of requests.current)
                controller.abort();
            requests.current.clear();
            reading.current = null;
            setState(previous => ({ ...(previous.owner === owner ? previous : initial(owner)), snapshot: null, fresh: false, loading: false, readBusy: false, error: 'You are offline. Reconnect before reading or answering an item.' }));
        };
        const timer = setInterval(visibleRefresh, 30000);
        document.addEventListener('visibilitychange', visibleRefresh);
        window.addEventListener('focus', visibleRefresh);
        window.addEventListener('online', visibleRefresh);
        window.addEventListener('offline', offline);
        return () => {
            ++sequence.current;
            for (const controller of requests.current)
                controller.abort();
            requests.current.clear();
            reading.current = null;
            clearInterval(timer);
            document.removeEventListener('visibilitychange', visibleRefresh);
            window.removeEventListener('focus', visibleRefresh);
            window.removeEventListener('online', visibleRefresh);
            window.removeEventListener('offline', offline);
        };
    }, [owner, enabled, accountKey, refresh]);
    async function applyRead(input, lookup) {
        if (reading.current || identity.current !== owner || !owner.enabled || !owner.accountKey || typeof navigator !== 'undefined' && !navigator.onLine)
            return;
        const operation = {}, controller = new AbortController();
        reading.current = operation;
        requests.current.add(controller);
        setState(previous => ({ ...(previous.owner === owner ? previous : initial(owner)), readBusy: true, pendingRead: input, error: '' }));
        try {
            const receipt = lookup ? await api.readReceipt(input.clientRequestId, controller.signal) : await api.read(input, controller.signal);
            if (controller.signal.aborted || identity.current !== owner)
                return;
            if (receipt.clientRequestId !== input.clientRequestId || receipt.read !== input.read || receipt.acknowledged !== input.items.length
                || (receipt.dismiss === true) !== (input.dismiss === true))
                throw new Error('Unverified read receipt');
            setState(previous => ({ ...previous, readBusy: false, pendingRead: null }));
            // Host cue delivery is advisory. It cannot turn an accepted durable receipt into an uncertain write.
            try {
                acknowledged.current?.();
            }
            catch { /* The next native poll will reconcile its cues. */ }
            await refresh();
        }
        catch (error) {
            if (controller.signal.aborted || identity.current !== owner)
                return;
            const definite = [400, 401, 403, 404, 409].includes(Number(statusOf(error)));
            setState(previous => ({ ...previous, fresh: false, readBusy: false, pendingRead: definite ? null : input,
                error: definite ? 'This item changed or is unavailable. Refreshing its current state.' : 'The read acknowledgement is not confirmed. Check its receipt before trying another update.' }));
            if (definite)
                await refresh();
        }
        finally {
            requests.current.delete(controller);
            if (reading.current === operation)
                reading.current = null;
        }
    }
    function markRead(items, read = true, dismiss = false) {
        const visible = latest.current;
        if (identity.current !== owner || !visible.fresh || visible.pendingRead || reading.current || !items.length || items.length > 100)
            return;
        // Refuse stale closures rather than silently rebinding an interaction to newer content.
        if (items.some(item => !visible.snapshot?.items.some(value => value.id === item.id && value.revision === item.revision && value.readRevision === item.readRevision)))
            return;
        const targets = [...new Map(items.map(item => [item.id, { id: item.id, revision: item.revision, readRevision: item.readRevision }])).values()];
        void applyRead({ clientRequestId: crypto.randomUUID(), read, ...(dismiss ? { dismiss: true } : {}), items: targets }, false);
    }
    /** Hide the items from this person's inbox everywhere (a read mark with dismiss; the same receipt path). */
    const dismiss = (items) => markRead(items, true, true);
    return { ...current, refresh, markRead, dismiss, reconcileRead: () => { if (current.pendingRead)
            void applyRead(current.pendingRead, true); } };
}
//# sourceMappingURL=useAttentionInbox.js.map